import { useEffect, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { EmployerLayout } from "@/components/employer/portal/EmployerLayout";
import { apiFetch, inputClass, Kicker, Label, PrimaryButton, Spinner, StatusPill } from "@/components/employer/portal/shared";

const SECTIONS = [
    {
        title: "Company",
        fields: [
            ["company_name", "Company name"],
            ["website", "Company website"],
            ["country", "Country"],
            ["city", "City"],
            ["business_address", "Business address"],
            ["industry", "Industry / sector"],
            ["company_size", "Company size"],
        ],
    },
    {
        title: "Primary Contact",
        fields: [
            ["contact_person", "Contact person"],
            ["business_email", "Business email"],
            ["phone", "Phone"],
            ["whatsapp", "WhatsApp"],
        ],
    },
    {
        title: "Recruitment Contact",
        fields: [
            ["recruitment_contact_name", "Recruitment contact"],
            ["recruitment_contact_email", "Recruitment email"],
            ["recruitment_contact_phone", "Recruitment phone"],
            ["registration_info", "Business registration information"],
        ],
    },
];

export default function EmployerProfile() {
    const [profile, setProfile] = useState(null);
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState("");

    const load = async () => {
        const res = await apiFetch("/employer/profile");
        if (res.ok) {
            const data = await res.json();
            setProfile(data);
            setForm(data);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const save = async () => {
        if (saving) return;
        setSaving(true);
        setSaved(false);
        setError("");
        const res = await apiFetch("/employer/profile", {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(form),
        });
        if (res.ok) {
            setSaved(true);
            await load();
        } else {
            const body = await res.json().catch(() => ({}));
            setError(typeof body.detail === "string" ? body.detail : Object.values(body.detail || {}).flat().join(" ") || "Could not save profile.");
        }
        setSaving(false);
    };

    const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

    return (
        <EmployerLayout title="Company Profile" subtitle="A complete, credible company profile helps Afrinexa verify your organization faster.">
            {!profile ? <Spinner /> : (
                <div className="grid gap-10 lg:grid-cols-[0.72fr_0.28fr]" data-testid="employer-profile-page">
                    <div className="space-y-10">
                        {SECTIONS.map((section) => (
                            <section key={section.title} className="border border-ink/10 bg-ivory p-6 sm:p-7">
                                <Kicker>{section.title}</Kicker>
                                <div className="mt-6 grid gap-5 sm:grid-cols-2">
                                    {section.fields.map(([key, label]) => (
                                        <div key={key} className={key === "business_address" || key === "registration_info" ? "sm:col-span-2" : ""}>
                                            <Label>{label}</Label>
                                            <input data-testid={`employer-profile-${key}`} type="text" value={form[key] || ""} onChange={set(key)} className={inputClass} />
                                        </div>
                                    ))}
                                </div>
                            </section>
                        ))}

                        <section className="border border-ink/10 bg-ivory p-6 sm:p-7">
                            <Kicker>Company Description</Kicker>
                            <div className="mt-6">
                                <Label>About the company</Label>
                                <textarea data-testid="employer-profile-description" rows={7} value={form.description || ""} onChange={set("description")} className={`${inputClass} resize-y`} placeholder="Tell Afrinexa about your organization, operations and hiring needs." />
                            </div>
                        </section>

                        {error && <p data-testid="employer-profile-error" className="border-l-2 border-champagne pl-4 text-sm text-ink/70">{error}</p>}
                        <div className="flex flex-wrap items-center gap-4">
                            <PrimaryButton data-testid="employer-profile-save" busy={saving} onClick={save}>Save Company Profile</PrimaryButton>
                            {saved && <span data-testid="employer-profile-saved" className="inline-flex items-center gap-1.5 text-sm text-forest"><CheckCircle2 size={15} /> Saved</span>}
                        </div>
                    </div>

                    <aside className="h-fit border border-ink/10 bg-night p-7 text-ivory lg:sticky lg:top-8">
                        <Kicker dark>Profile strength</Kicker>
                        <p className="mt-6 font-serif text-5xl font-medium">{profile.completion}%</p>
                        <div className="mt-5 h-1.5 bg-ivory/10">
                            <div data-testid="employer-profile-completion" className="h-full bg-champagne transition-all duration-700" style={{ width: `${profile.completion}%` }} />
                        </div>
                        <div className="mt-7 border-t border-ivory/10 pt-6">
                            <StatusPill status={profile.verification_status} type="verification" dark />
                            <p className="mt-4 text-sm leading-relaxed text-ivory/55">
                                Verification is reviewed by Afrinexa. New employer accounts are never verified automatically.
                            </p>
                            <a href="/employer/verification" data-testid="profile-verification-link" className="mt-6 inline-flex border border-ivory/25 px-5 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-ivory transition-colors hover:border-champagne hover:text-champagne">
                                Review verification
                            </a>
                        </div>
                    </aside>
                </div>
            )}
        </EmployerLayout>
    );
}
